import React, { useCallback, useEffect, useRef, useState } from 'react';
import Button from '../../../../composants/Button/Button';
import './TabQuestionsReponses.css';

interface Reponse {
  id: number;
  contenu: string;
  auteur_type: 'admin' | 'prestataire';
  is_mine: boolean;
  created_at: string;
}

interface Question {
  id: number;
  numero: number;
  identifiant_candidature?: string | null;
  contenu: string;
  auteur_type: 'admin' | 'prestataire';
  is_mine: boolean;
  created_at: string;
  reponses: Reponse[];
}

interface FlatMessage {
  id: number;
  contenu: string;
  auteur_type: 'admin' | 'prestataire';
  is_mine: boolean;
  created_at: string;
  numero?: number | null;
  identifiant_candidature?: string | null;
}

interface FilQuestionsReponsesProps {
  accordCadreId: number;
  /** Masque la zone de saisie. La lecture du fil est publique, l'envoi ne l'est pas. */
  lectureSeule?: boolean;
}

const MAX_CHARS = 2000;

const formatDate = (iso: string): string => {
  const d = new Date(iso);
  return (
    d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' }) +
    ' à ' +
    d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
  );
};

const getAuthorLabel = (msg: FlatMessage): string => {
  if (msg.auteur_type === 'admin') return 'Centralis';
  if (msg.numero != null) return `Question ${msg.numero} - Candidature ${msg.identifiant_candidature}`;
  return msg.identifiant_candidature ? `Candidature ${msg.identifiant_candidature}` : 'Candidat';
};

const flattenMessages = (questions: Question[]): FlatMessage[] => {
  const flat: FlatMessage[] = [];
  for (const q of questions) {
    flat.push({
      id: q.id,
      contenu: q.contenu,
      auteur_type: q.auteur_type,
      is_mine: q.is_mine,
      created_at: q.created_at,
      numero: q.numero,
      identifiant_candidature: q.identifiant_candidature,
    });
    for (const r of q.reponses ?? []) {
      flat.push({ id: r.id, contenu: r.contenu, auteur_type: r.auteur_type, is_mine: r.is_mine, created_at: r.created_at });
    }
  }
  const sorted = flat.sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());

  // Renuméroter les questions prestataire séquentiellement (les questions admin
  // ont aussi un numero mais s'affichent comme "Centralis" sans numéro visible)
  let questionCounter = 0;
  for (const msg of sorted) {
    if (msg.numero != null && msg.auteur_type === 'prestataire') {
      questionCounter++;
      msg.numero = questionCounter;
    }
  }

  return sorted;
};

/** En-tetes d'appel : le token n'est joint que s'il existe, la lecture etant publique. */
const buildHeaders = (): HeadersInit => {
  const token = localStorage.getItem('token');
  return token
    ? { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
    : { 'Content-Type': 'application/json' };
};

const FilQuestionsReponses: React.FC<FilQuestionsReponsesProps> = ({ accordCadreId, lectureSeule = false }) => {
  const apiUrl = process.env.REACT_APP_API_URL;

  const [questions, setQuestions]   = useState<Question[]>([]);
  const [loading, setLoading]       = useState(true);
  const [loadError, setLoadError]   = useState<string | null>(null);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending]       = useState(false);
  const [sendError, setSendError]   = useState<string | null>(null);

  const threadEndRef = useRef<HTMLDivElement>(null);

  const loadQuestions = useCallback(async () => {
    if (!accordCadreId) return;
    setLoading(true);
    setLoadError(null);
    try {
      const res = await fetch(
        `${apiUrl}/publications/ac27/accords-cadres/${accordCadreId}/questions`,
        { headers: buildHeaders() }
      );
      if (!res.ok) throw new Error(`Erreur ${res.status}`);
      const json = await res.json();
      setQuestions(json.data ?? []);
    } catch (err: any) {
      setLoadError(err.message);
    } finally {
      setLoading(false);
    }
  }, [accordCadreId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { loadQuestions(); }, [loadQuestions]);

  useEffect(() => {
    threadEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [questions]);

  const handleSubmit = async () => {
    const contenu = newMessage.trim();
    if (!contenu || sending) return;
    setSending(true);
    setSendError(null);
    try {
      const res = await fetch(
        `${apiUrl}/publications/ac27/accords-cadres/${accordCadreId}/questions`,
        { method: 'POST', headers: buildHeaders(), body: JSON.stringify({ contenu }) }
      );
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.message ?? `Erreur ${res.status}`);
      }
      setNewMessage('');
      await loadQuestions();
    } catch (err: any) {
      setSendError(err.message);
    } finally {
      setSending(false);
    }
  };

  const messages = flattenMessages(questions);

  return (
    <div className="qr-thread-container">
      <div className="qr-thread">
        {loading && (
          <div className="qr-loading">
            <div className="spinner" />
            <span>Chargement des messages...</span>
          </div>
        )}

        {loadError && <div className="qr-error">Impossible de charger les messages.</div>}

        {!loading && !loadError && messages.length === 0 && (
          <div className="qr-empty">
            {lectureSeule ? 'Aucune question pour le moment.' : 'Aucune question pour le moment. Posez la première !'}
          </div>
        )}

        {!loading && !loadError && messages.map((msg) => (
          <div
            key={msg.id}
            className={`qr-bubble ${msg.is_mine ? 'qr-bubble--mine' : ''} ${msg.auteur_type === 'admin' ? 'qr-bubble--admin' : 'qr-bubble--prestataire'}`}
          >
            <div className="qr-bubble-header">
              <span className="qr-bubble-author">{getAuthorLabel(msg)}</span>
              <span className="qr-bubble-date">{formatDate(msg.created_at)}</span>
            </div>
            <div className="qr-bubble-body">{msg.contenu}</div>
          </div>
        ))}

        <div ref={threadEndRef} />
      </div>

      {!lectureSeule && (
        <div className="qr-input-bar">
          <textarea
            className="qr-input-textarea"
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value.slice(0, MAX_CHARS))}
            placeholder="Votre message..."
            maxLength={MAX_CHARS}
            disabled={sending}
            rows={2}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSubmit();
              }
            }}
          />
          <div className="qr-input-actions">
            <span className="qr-char-count">{newMessage.length} / {MAX_CHARS}</span>
            {sendError && <span className="qr-send-error">{sendError}</span>}
            <Button
              onClick={handleSubmit}
              value={sending ? 'Envoi...' : 'Envoyer'}
              size="small"
              disabled={sending || newMessage.trim().length === 0}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default FilQuestionsReponses;
